import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  RefreshCw,
  Search,
  Camera,
  Hammer,
  Zap,
  Droplet,
  Square as SquareIcon,
  Wrench,
  Paintbrush,
  Snowflake,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICON: Record<string, any> = {
  lumber: Hammer,
  electrical: Zap,
  plumbing: Droplet,
  glass: SquareIcon,
  aluminum: SquareIcon,
  hardware: Wrench,
  paint: Paintbrush,
  concrete: SquareIcon,
  hvac: Snowflake,
  tools: Wrench,
  other: Package,
};

interface Props {
  jobId: string;
}

export function MaterialsTab({ jobId }: Props) {
  const qc = useQueryClient();
  const [polling, setPolling] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["supplier_line_items", jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_line_items" as any)
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as any[]) ?? [];
    },
  });

  const { data: emails = [] } = useQuery({
    queryKey: ["supplier_emails", jobId],
    queryFn: async () => {
      const { data } = await supabase
        .from("supplier_emails" as any)
        .select("*")
        .eq("job_id", jobId)
        .order("received_at", { ascending: false });
      return (data as any[]) ?? [];
    },
  });

  const cacheIds = Array.from(
    new Set(items.map((i: any) => i.product_cache_id).filter(Boolean)),
  );
  const { data: cache = [] } = useQuery({
    queryKey: ["product_cache", cacheIds.join(",")],
    queryFn: async () => {
      if (!cacheIds.length) return [];
      const { data } = await supabase
        .from("product_cache" as any)
        .select("*")
        .in("id", cacheIds);
      return (data as any[]) ?? [];
    },
    enabled: cacheIds.length > 0,
  });
  const cacheMap = new Map(cache.map((c: any) => [c.id, c]));

  const total = items.reduce(
    (sum: number, i: any) => sum + (Number(i.total_price) || 0),
    0,
  );

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data, error } = await supabase.functions.invoke("receipts-gmail-poll");
      if (error) throw error;
      toast.success(`Scanned ${data?.scanned ?? 0} emails, imported ${data?.inserted ?? 0}`);
      qc.invalidateQueries({ queryKey: ["supplier_line_items", jobId] });
      qc.invalidateQueries({ queryKey: ["supplier_emails", jobId] });
    } catch (e: any) {
      toast.error(e?.message ?? "Poll failed");
    } finally {
      setPolling(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" /> Materials Ledger
          </h2>
          <p className="text-sm text-muted-foreground">
            Auto-imported supplier receipts. Total to date:{" "}
            <span className="font-semibold text-foreground">
              ${total.toFixed(2)}
            </span>
          </p>
        </div>
        <Button onClick={pollNow} disabled={polling} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${polling ? "animate-spin" : ""}`} />
          Sync Gmail
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No supplier line items on this job yet. Forward or send a Home
            Depot / Lowe's / Ferguson / ABC Supply receipt to your Gmail, then
            hit <span className="font-semibold">Sync Gmail</span>.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((li: any) => {
            const p = li.product_cache_id
              ? (cacheMap.get(li.product_cache_id) as any)
              : null;
            const Icon = CATEGORY_ICON[li.category ?? "other"] ?? Package;
            return (
              <Card key={li.id}>
                <CardContent className="flex items-center gap-4 py-3">
                  <div className="w-16 h-16 rounded bg-muted flex items-center justify-center overflow-hidden shrink-0">
                    {p?.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.official_name ?? li.description ?? ""}
                        className="w-full h-full object-cover"
                      />
                    ) : li.custom_photo_url ? (
                      <img
                        src={li.custom_photo_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon className="h-7 w-7 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">
                      {p?.official_name ?? li.description ?? "Unnamed item"}
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                      {li.sku && <span>SKU: {li.sku}</span>}
                      {li.vendor && <span>{li.vendor}</span>}
                      <Badge variant="secondary" className="capitalize">
                        {li.category ?? "other"}
                      </Badge>
                    </div>
                    {!p && (
                      <div className="mt-1 flex gap-2">
                        <a
                          href={`https://www.homedepot.com/s/${encodeURIComponent(
                            li.sku ?? li.description ?? "",
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Search className="h-3 w-3" /> Search Home Depot
                        </a>
                        <button
                          onClick={() =>
                            toast.info("Photo upload coming next iteration")
                          }
                          className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Camera className="h-3 w-3" /> Add photo
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold">
                      ${Number(li.total_price ?? 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {li.qty ?? 1} × ${Number(li.unit_price ?? 0).toFixed(2)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {emails.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Communications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {emails.map((e: any) => (
              <div key={e.id} className="flex items-center justify-between border-b py-1.5 last:border-0">
                <div className="min-w-0">
                  <div className="font-medium truncate">{e.subject ?? "(no subject)"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {e.from_email} · {e.vendor ?? ""}{" "}
                    {e.invoice_number ? `· #${e.invoice_number}` : ""}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-3">
                  {e.received_at ? new Date(e.received_at).toLocaleDateString() : ""}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
